import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useAccessToken } from '@hooks/useAccessToken';
import { USERS_ME_QUERY_KEY } from '@hooks/useProfile';
import { TRANSFER_RECOMMENDATIONS_QUERY_KEY } from '@hooks/useTransferRecommendations';
import { upsertPlanner } from '@api-service/planner';
import { updateFplTeamId } from '@api-service/profile';

const PLANNER_STATE_QUERY_KEY = ['plannerState'] as const;

// The backend import pulls the squad from FPL server-side and can hang for a
// long time when that upstream call is struggling; without a timeout the
// import modal sits on "Importing..." indefinitely.
const IMPORT_TIMEOUT_MS = 30_000;

/**
 * Switches the user's FPL team. When given a fplTeamId, sets it on the
 * profile first and then re-imports the planner from FPL. With no argument
 * it just re-imports the planner using whatever ID is already on the
 * profile.
 */
export const useImportFromFpl = () => {
  const { accessToken } = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (fplTeamId?: number) => {
      if (!accessToken) {
        throw new Error('No access token available');
      }

      // AbortController + setTimeout rather than AbortSignal.timeout, which
      // jsdom (and older browsers) do not implement.
      const abortController = new AbortController();
      const timeoutId = setTimeout(
        () => abortController.abort(),
        IMPORT_TIMEOUT_MS
      );

      try {
        if (fplTeamId !== undefined) {
          const profileResult = await updateFplTeamId(
            accessToken,
            fplTeamId,
            abortController.signal
          );
          if (!profileResult.ok) {
            throw new Error(profileResult.message);
          }
        }

        await upsertPlanner({
          accessToken,
          body: { source: 'FPL' },
          signal: abortController.signal
        });
      } finally {
        clearTimeout(timeoutId);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PLANNER_STATE_QUERY_KEY });
      // Prefix match: useProfile keys are ['users-me', accessToken].
      queryClient.invalidateQueries({ queryKey: USERS_ME_QUERY_KEY });
      // AI transfer recommendations are solved against the planner squad, so
      // a re-import must also throw away suggestions for the old team.
      queryClient.invalidateQueries({
        queryKey: TRANSFER_RECOMMENDATIONS_QUERY_KEY
      });
    }
  });
};
